/**
 * @file One gate for every automatic full rejoin asked for by a recovery
 * ladder (#2223 AC46, #2224 not-heard).
 *
 * Two ladders can ask for a rejoin: the speaker's "not heard" ladder
 * (notHeardRunner.ts) and the listener's "can't hear" ladder (audibility.ts).
 * Both go through the single entry point registered in `useNotHeardStore`
 * (signed-in: the reconnect controller; guest: useGuestReconnect). That entry
 * point already refuses while a rejoin is running; this gate adds the other
 * half -- a rejoin that *just finished* blocks another for REJOIN_COOLDOWN_MS,
 * so the two ladders never rejoin back to back and neither loops.
 *
 * Module state on purpose: the guest page remounts everything inside
 * <LiveKitRoom> on a rejoin, so a ladder's own memory does not survive it.
 */
import { useNotHeardStore } from "../store/notHeardStore";

/** No second automatic rejoin within this long of the last one. */
export const REJOIN_COOLDOWN_MS = 30_000;

let lastRejoinAt: number | null = null;

export type RejoinOutcome = "started" | "recent" | "refused";

/** Start a full rejoin unless one ran recently or none can start. */
export function requestGatedRejoin(at: number = Date.now(), { force = false }: { force?: boolean } = {}): RejoinOutcome {
  // `force`: the user asked (a button press) -- never second-guess that.
  if (!force && lastRejoinAt !== null && at - lastRejoinAt < REJOIN_COOLDOWN_MS) return "recent";
  const rejoin = useNotHeardStore.getState().rejoin;
  let started = false;
  try {
    started = !!rejoin && rejoin();
  } catch {
    started = false;
  }
  if (!started) return "refused";
  lastRejoinAt = at;
  return "started";
}

/** Tests only. */
export function resetRejoinGate(): void {
  lastRejoinAt = null;
}
